import React from "react";
import { Navigate, useParams } from "react-router-dom";
import Icon from "components/AppIcon";
import RccIndicatorSetupWorkspace from "components/setup/RccIndicatorSetupWorkspace";
import OhlgsCatalogSetupWorkspace from "components/setup/OhlgsCatalogSetupWorkspace";
import useAuth from "hooks/useAuth";
import { SETUP_MODULES_BY_SLUG, getSetupPath } from "config/setupModules";
import { canAccessSetupModule, getDefaultAllowedSetupModuleSlug } from "utils/setupAccess";
import DpatIndicatorsSetupWorkspace from "components/setup/DpatIndicatorsSetupWorkspace";

export default function SetupModulePage() {
  const { user } = useAuth();
  const { setupModule } = useParams();
  const activeModule = SETUP_MODULES_BY_SLUG[setupModule];
  const defaultAllowedModuleSlug = getDefaultAllowedSetupModuleSlug(user);

  if (!activeModule || !canAccessSetupModule(activeModule.slug, user)) {
    if (defaultAllowedModuleSlug) {
      return <Navigate to={getSetupPath(defaultAllowedModuleSlug)} replace />;
    }

    return <Navigate to="/ghana/dashboard" replace />;
  }

  if (activeModule.slug === "rcc-indicator-setup") {
    return <RccIndicatorSetupWorkspace activeModule={activeModule} />;
  }

  if (activeModule.slug === "ohlgs-setup") {
    return <OhlgsCatalogSetupWorkspace activeModule={activeModule} />;
  }

  if (activeModule.slug === "dpat-indicator-setup") {
    return <DpatIndicatorsSetupWorkspace activeModule={activeModule} />;
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <section className="rounded-2xl border border-border bg-card p-6 shadow-sm">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <div className="max-w-2xl">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground">
              <Icon name={activeModule.icon} className="h-3.5 w-3.5" />
              Setup Workspace
            </div>
            <h2 className="text-2xl font-semibold text-foreground">{activeModule.label}</h2>
            <p className="mt-2 text-sm text-muted-foreground">{activeModule.summary}</p>
          </div>

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            {activeModule.highlights.map((item) => (
              <div
                key={item}
                className="rounded-xl border border-border bg-background px-4 py-3 text-sm font-medium text-foreground"
              >
                {item}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="grid gap-4 lg:grid-cols-3">
        {activeModule.panels.map((panel) => (
          <article key={panel.title} className="rounded-2xl border border-border bg-card p-5 shadow-sm">
            <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Icon name={activeModule.icon} className="h-4 w-4" />
            </div>
            <h3 className="text-lg font-semibold text-foreground">{panel.title}</h3>
            <p className="mt-2 text-sm text-muted-foreground">{panel.description}</p>
          </article>
        ))}
      </section>

      <section className="rounded-2xl border border-dashed border-border bg-card p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-foreground">Configuration Notes</h3>
        <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
          This setup section is ready for the actual configuration forms and workflow actions. Wire the final
          controls into this page when the setup endpoints and validation rules are confirmed.
        </p>
      </section>
    </div>
  );
}
